import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import DateTimeline from '../components/philipSnat/DateTimeline';
import PredictionLeagueRow from '../components/philipSnat/PredictionLeagueRow';
import { isSoccerLeague, leagueLabel } from '../constants/philipSnatLeagues';
import { useTranslation } from '../contexts/TranslationContext';
import { SportIcon } from '../utils/sportIcons';
import { apiGet, downloadFile } from '../utils/api';

interface PredictionFile {
  id: string;
  name: string;
  date: string;
  sport: string;
  games: number;
}

const TIMELINE_PAST_DAYS = 3;
const TIMELINE_FUTURE_DAYS = 1;

const toDayKey = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const buildTimeline = (): string[] => {
  const days: string[] = [];
  for (let offset = -TIMELINE_PAST_DAYS; offset <= TIMELINE_FUTURE_DAYS; offset++) {
    const date = new Date();
    date.setDate(date.getDate() + offset);
    days.push(toDayKey(date));
  }
  return days;
};

const PhilipSnatModels: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [files, setFiles] = useState<PredictionFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState('');

  const days = useMemo(buildTimeline, []);
  const today = days[TIMELINE_PAST_DAYS];
  const available = useMemo(() => new Set(files.map((f) => f.date)), [files]);

  useEffect(() => {
    apiGet<PredictionFile[]>('/philip-snat/prediction-files')
      .then(setFiles)
      .catch((err) => console.error('Failed to fetch files:', err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (selectedDay && available.has(selectedDay)) return;
    const distance = (day: string) => {
      const offset = days.indexOf(day) - TIMELINE_PAST_DAYS;
      return offset >= 0 ? offset * 2 : -offset * 2 + 1;
    };
    const byDistance = [...days].sort((a, b) => distance(a) - distance(b));
    setSelectedDay(byDistance.find((day) => available.has(day)) ?? today);
  }, [available, days, today, selectedDay]);

  const dayFiles = files
    .filter((f) => f.date === selectedDay)
    .sort((a, b) => leagueLabel(a.sport).localeCompare(leagueLabel(b.sport)));
  const sections = [
    {
      key: 'hockey',
      title: t.philipSnat.hockey,
      icon: 'PiHockeyLight',
      files: dayFiles.filter((f) => !isSoccerLeague(f.sport)),
    },
    {
      key: 'soccer',
      title: t.philipSnat.soccer,
      icon: 'PiSoccerBallLight',
      files: dayFiles.filter((f) => isSoccerLeague(f.sport)),
    },
  ].filter((section) => section.files.length > 0);

  const handleDownload = async (file: PredictionFile) => {
    try {
      await downloadFile(`/philip-snat/prediction-files/${file.id}/download`, file.name);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  return (
    <div className="philip-snat-models">
      <Helmet>
        <title>AI Prediction Models | PhilipSnat</title>
        <meta name="description" content="Explore hockey and football match predictions generated by PhilipSnat's original AI models. Download CSV reports and track model accuracy." />
        <meta property="og:title" content="AI Prediction Models | PhilipSnat" />
        <meta property="og:description" content="Explore hockey and football match predictions generated by PhilipSnat's original AI models." />
        <meta property="og:url" content="https://philipsnat.com/philip-snat-models" />
        <link rel="canonical" href="https://philipsnat.com/philip-snat-models" />
      </Helmet>
      <div className="philip-snat-models__container">
        <div className="philip-snat-models__header">
          <div className="philip-snat-models__heading">
            <h1 className="page-header-title italic-title">{t.philipSnat.modelsTitle}</h1>
            <p className="philip-snat-models__subtitle">{t.philipSnat.modelsSubtitle}</p>
          </div>
          <button
            className="button_primary philip-snat-models__stats-button"
            onClick={() => navigate('/philip-snat-models/stats')}
          >
            {t.philipSnat.statsButton}
          </button>
        </div>

        <DateTimeline
          days={days}
          available={available}
          selected={selectedDay}
          today={today}
          onSelect={setSelectedDay}
        />

        {loading ? (
          <p className="philip-snat-models__empty">{t.common.loading}</p>
        ) : sections.length > 0 ? (
          sections.map((section) => (
            <section key={section.key} className="philip-snat-models__section">
              <h2 className="philip-snat-models__section-title">
                <SportIcon name={section.icon} size={22} />
                {section.title}
              </h2>
              <div className="philip-snat-models__rows">
                {section.files.map((file) => (
                  <PredictionLeagueRow
                    key={file.id}
                    league={file.sport}
                    games={file.games}
                    onView={() => navigate(`/philip-snat-models/${file.id}`)}
                    onDownload={() => handleDownload(file)}
                  />
                ))}
              </div>
            </section>
          ))
        ) : (
          <p className="philip-snat-models__empty">{t.philipSnat.noCsvFiles}</p>
        )}
      </div>
    </div>
  );
};

export default PhilipSnatModels;
